import {
  AfterViewInit,
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { DialogCloseReason, DialogService } from '@sui/dialog';
import { NomObjectClass, NomObjectTypes } from 'src/app/core/constants';
import { filter, map, switchMap, take, tap } from 'rxjs/operators';

import { DataObject } from 'src/app/core/models/core.models';
import { EMPTY } from 'rxjs';
import { OtherFeedsOperation } from 'src/app/core/models/widget-operations';
import { PickerDataobjectsDialogComponent } from 'src/app/core/components/popups/picker-dataobjects-dialog/picker-dataobjects-dialog.component';
import { TranslateService } from '@ngx-translate/core';
import { UserObjectsService } from 'src/app/core/services/user-objects.service';
import { WidgetMoveAction } from '../move-copy-widget-dialog.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-move-copy-other-feeds',
  templateUrl: './move-copy-other-feeds.component.html',
  styleUrl: './move-copy-other-feeds.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class MoveCopyOtherFeedsComponent implements OnInit, AfterViewInit {
  @Input() widgets: DataObject[] = [];
  @Input() currentObject?: DataObject;
  @Output() formValidityChange = new EventEmitter<boolean>();

  globalAction: WidgetMoveAction = 'copy';
  switchDatasource: boolean = false;
  selectedFeeds: DataObject[] = [];
  selectedFeedIds: string[] = [];

  private translateService = inject(TranslateService);
  private dialogService = inject(DialogService);
  private destroyRef = inject(DestroyRef);

  constructor(private userObjects: UserObjectsService) {}

  ngOnInit(): void {}

  ngAfterViewInit(): void {
    // * initially invalid until Feeds are selected
    setTimeout(() => {
      this.emitValidity();
    });
  }

  private emitValidity(): void {
    this.formValidityChange.emit(this.selectedFeedIds.length > 0);
  }

  onGlobalActionChange(action: WidgetMoveAction): void {
    this.globalAction = action;
  }

  getActionDisplayText(action: WidgetMoveAction): string {
    return this.translateService.instant(
      action === 'copy'
        ? 'move-copy-widget-dialog.label.action-copy'
        : 'move-copy-widget-dialog.label.action-move',
    );
  }

  openFeedPicker(): void {
    const dialogRef = this.dialogService.openTabsDialog(
      PickerDataobjectsDialogComponent,
      {
        data: {
          objectClassIds: [NomObjectClass.FEED],
          objectTypeIds: [
            NomObjectTypes.ONLINE_FEED,
            NomObjectTypes.SOCIAL_FEED,
            NomObjectTypes.CLIPPING_FEED,
            NomObjectTypes.BROADCAST_FEED,
            NomObjectTypes.COMBINED_FEED,
          ],
          selectedObjectIds: new Map(
            this.selectedFeeds.map((feed) => [
              feed.id,
              {
                object_id: feed.id,
                object_class_id: feed.object_class_id,
              },
            ]),
          ),
        },
        width: '61vw',
        minWidth: 1220,
        maxWidth: 1550,
        height: '70vh',
        closeOnClick: false,
        closeOnEscape: true,
      },
    );

    dialogRef.closed
      .pipe(
        take(1),
        filter(
          (event: any) =>
            event?.reason === DialogCloseReason.Save ||
            event?.reason === DialogCloseReason.SaveAndClose,
        ),
        map((event: any) => event.data as string[]),
        tap((feedIds: string[]) => {
          // Filter out current Feed
          this.selectedFeedIds = feedIds.filter(
            (id) => id !== this.currentObject?.id,
          );
        }),
        switchMap((feedIds: string[]) => {
          if (feedIds.length === 0) {
            this.selectedFeeds = [];
            this.emitValidity();
            return EMPTY;
          }

          return this.userObjects.objectsByIds$(feedIds).pipe(
            take(1),
            tap((feeds) => {
              this.selectedFeeds = feeds;
              this.emitValidity();
            }),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  removeFeed(feedId: string): void {
    this.selectedFeedIds = this.selectedFeedIds.filter((id) => id !== feedId);
    this.selectedFeeds = this.selectedFeeds.filter(
      (feed) => feed.id !== feedId,
    );
    this.emitValidity();
  }

  getFormData(): OtherFeedsOperation[] {
    if (!this.currentObject || this.selectedFeedIds.length === 0) return [];

    return this.widgets.map((widget) => ({
      scenario: 'other-feeds',
      widget_id: widget.id,
      source_object_id: this.currentObject!.id,
      source_object_class_id: this.currentObject!.object_class_id,
      target_object_ids: this.selectedFeedIds as [string, ...string[]],
      target_object_class_id: NomObjectClass.FEED,
      action: this.globalAction,
      switch_datasource: this.switchDatasource,
    }));
  }
}
